// Three.js
import * as THREE from 'three';

// Global
import { isPointInPolygon, polygonHasSelfIntersection } from '@data/geometry/Functions';
import FVector from '@data/geometry/FVector';
import useNotificationStore from '@gui/store/notification';
import { NotificationType } from '@visualizer/types';
import { getViewer } from 'store/viewerStore';
import { EventHandler } from './EventHandlers';
import { PolygonDrawerManager } from './PolygonDrawerManager';
import { VisualEffects } from './VisualEffects';

class SimpleEmitter {
    private listeners: Record<string, Function[]> = {};

    on(event: string, cb: Function) {
        (this.listeners[event] ??= []).push(cb);
    }

    off(event: string, cb: Function) {
        this.listeners[event] = (this.listeners[event] ?? []).filter((fn) => fn !== cb);
    }

    emit(event: string, ...args: any[]) {
        (this.listeners[event] ?? []).forEach((fn) => fn(...args));
    }
}

export class DrawModule extends SimpleEmitter {
    points: FVector[];
    running: boolean;
    editing: boolean;
    minNumPoints: number;
    closeRadius: number;

    onSaveCallback: (points: FVector[], failed: boolean) => void;
    onInvalidPoint: () => void;
    onSelfIntersection: () => void;
    onSelfIntersectionSave: () => void;
    onInvalidDeletion: (minNumPoints: number) => void;

    constrainPolygon: FVector[];

    closeRadiusIndicator: THREE.Mesh | null;
    isShowingCloseIndicator: boolean;

    selectedPointIndex: number | null;
    pointClickThreshold: number;
    ignoreNextClick: boolean;

    eventHandler: EventHandler;
    visualEffects: VisualEffects;

    constructor(onSave: (points: FVector[], failed: boolean) => void, constrainPolygon: FVector[]) {
        super();

        // Fields:
        this.points = [];
        this.running = false;
        this.editing = false;
        this.minNumPoints = 3;
        this.closeRadius = 2.0 / 3.0;

        // Close radius indicator:
        this.closeRadiusIndicator = null;
        this.isShowingCloseIndicator = false;

        // Point interaction:
        this.selectedPointIndex = null;
        this.pointClickThreshold = 1;
        this.ignoreNextClick = false;

        // React callbacks:
        this.onSaveCallback = onSave;
        const { showNotification } = useNotificationStore.getState();
        this.onInvalidPoint = () => showNotification('Point is outside the Analysis Area!', NotificationType.Error);
        this.onSelfIntersection = () => showNotification('Self-intersecting polygons are not allowed', NotificationType.Error);
        this.onSelfIntersectionSave = () =>
            showNotification('Tried to save a self-intersecting polygon, draw another polygon please.', NotificationType.Error);
        this.onInvalidDeletion = (minNumPoints) =>
            showNotification(`Cannot delete point: minimum ${minNumPoints} points required`, NotificationType.Warning);

        this.constrainPolygon = constrainPolygon;

        // Initialize helpers:
        this.eventHandler = new EventHandler(this);
        this.visualEffects = new VisualEffects(this.closeRadius);

        // Register in PolygonDrawerManager:
        PolygonDrawerManager.getInstance().register(this);
    }

    isInsidePolygon(point: FVector) {
        if (this.constrainPolygon && this.constrainPolygon.length == 0) return true; // Empty array = enabled everywhere.
        if (!this.constrainPolygon || this.constrainPolygon.length < 3) return undefined;

        return isPointInPolygon(point, this.constrainPolygon);
    }

    // Helper for safely start with Manager permission:
    async tryActivate(): Promise<boolean> {
        const manager = PolygonDrawerManager.getInstance();
        const ok = await manager.requestActivation(this);
        if (ok) {
            this.start(); // your existing method that begins drawing
            return true;
        }
        return false;
    }

    dispose(destroy: boolean = false) {
        PolygonDrawerManager.getInstance().unregister(this, destroy);
        this.eventHandler.dispose();
        this.visualEffects.dispose();
        this.points = [];
        this.emit('pointsChanged', this.points);
    }

    // =====================================
    // CALLBACKS
    // =====================================

    setOnInvalidPointCallback(cb: () => void) {
        this.onInvalidPoint = cb;
    }

    setOnInvalidDeletionCallback(cb: (minNumPoints: number) => void) {
        this.onInvalidDeletion = cb;
    }

    setOnSelfIntersectionCallback(cb: () => void) {
        this.onSelfIntersection = cb;
    }

    setOnSaveCallback(cb: (points: FVector[], failed: boolean) => void) {
        this.onSaveCallback = cb;
    }

    // =====================================
    // POINT MANAGEMENT
    // =====================================

    addPoint(position: FVector, insertIndex?: number) {
        if (this.ignoreNextClick) {
            this.ignoreNextClick = false;
            return;
        }

        // Check if point is inside a boundary:
        if (!this.isInsidePolygon(position)) {
            this.visualEffects.showInvalidClickIndicator?.(position);
            this.onInvalidPoint?.();
            return;
        }

        // Self-Intersection check:
        if (this.onSelfIntersection && this.points.length + 1 >= 3) {
            const candidate = position.clone();
            const prospective =
                insertIndex !== undefined
                    ? [...this.points.slice(0, insertIndex), candidate, ...this.points.slice(insertIndex)]
                    : [...this.points, candidate];

            if (polygonHasSelfIntersection(prospective.map((p) => new FVector(p.x, p.y, p.z)))) {
                this.visualEffects.showInvalidClickIndicator?.(position);
                this.onSelfIntersection?.();
                return;
            }
        }

        // Add point & sphere at same index:
        const point = position.clone();
        if (insertIndex !== undefined) {
            this.points.splice(insertIndex, 0, point);
            this.visualEffects.createSphere(point, insertIndex);
        } else {
            this.points.push(point);
            this.visualEffects.createSphere(point);
        }
        const closedPoints = [...this.points, this.points[0]];
        this.visualEffects.generatePolyLine(this.editing ? closedPoints : this.points);

        // Notify React:
        this.emit('pointsChanged', this.getPolygonPositions());
    }

    deletePoint(index: number) {
        if (index < 0 || index >= this.points.length) return;

        // Deactivate transform controls if the same vertex was selected
        if (this.selectedPointIndex === index) {
            this.eventHandler.deactivateTransformControls();
            this.selectedPointIndex = null;
        }

        // Optional: enforce minimum polygon size
        if (this.points.length <= this.minNumPoints) {
            this.onInvalidDeletion?.(this.minNumPoints);
            return;
        }

        // Remove the point:
        this.points.splice(index, 1)[0];

        // Remove corresponding sphere:
        const spheres = this.visualEffects.getSpheres();
        if (spheres && spheres[index]) {
            const sphere = spheres[index];
            getViewer().scene?.remove(sphere);
            sphere.geometry.dispose();
            (sphere.material as THREE.Material).dispose();
            spheres.splice(index, 1);
        }

        // Rebuild visuals:
        this.visualEffects.removePolyLine();
        const closedPoints = [...this.points, this.points[0]];
        this.visualEffects.generatePolyLine(this.editing ? closedPoints : this.points);

        // Notify React:
        this.emit('pointsChanged', this.getPolygonPositions());
    }

    insertPointOnLine(segmentIndex: number, newPoint: FVector) {
        this.addPoint(newPoint, segmentIndex + 1);
    }

    setPoints(newPoints: FVector[]) {
        if (newPoints.length === 0) return;

        this.visualEffects.removePolyLine();

        // Prepare prospective FVector list (without mutating existing points):
        const candidatePoints = newPoints.map((p) => new FVector(p.x, p.y, p.z));

        // Inside-boundary check:
        const allInside = candidatePoints.every((p) => this.isInsidePolygon(p));
        if (!allInside) {
            const invalidPoint = candidatePoints.find((p) => !this.isInsidePolygon(p));
            if (invalidPoint) {
                this.visualEffects.showInvalidClickIndicator?.(invalidPoint);
            }
            this.onInvalidPoint?.();
            return; // Abort — keep existing polygon
        }

        // Self-intersection check:
        if (this.onSelfIntersection && candidatePoints.length >= 3) {
            const fVecs = candidatePoints.map((p) => new FVector(p.x, p.y, p.z));
            if (polygonHasSelfIntersection(fVecs)) {
                this.visualEffects.showInvalidClickIndicator?.(candidatePoints[0]);
                this.onSelfIntersection?.();
                return; // Abort — keep existing polygon
            }
        }

        // Detect if the selected point has actually changed:
        let shouldDeactivateTransform = false;
        if (
            this.selectedPointIndex !== null &&
            this.selectedPointIndex >= 0 &&
            this.selectedPointIndex < this.points.length &&
            this.points.length === candidatePoints.length
        ) {
            const prev = this.points[this.selectedPointIndex];
            const next = candidatePoints[this.selectedPointIndex];

            // Compare with a small epsilon to avoid float noise
            const epsilon = 1e-6;
            const moved = Math.abs(prev.x - next.x) > epsilon || Math.abs(prev.y - next.y) > epsilon || Math.abs(prev.z - next.z) > epsilon;

            if (moved) shouldDeactivateTransform = true;
        }

        // Passed all checks → safely assign:
        this.points = candidatePoints;

        // Set editing status (this is needed when the page is refreshed and points are set):
        this.editing = this.points.length >= 3 ? true : false;
        this.visualEffects.setIsEditing(this.editing);

        // Deactivate transform controls if a point was selected and it's the one being moved:
        if (shouldDeactivateTransform) {
            this.eventHandler.deactivateTransformControls();
        }

        // Rebuild visuals:
        this.rebuildVisualElements();
        const closedPoints = [...this.points, this.points[0]];
        this.visualEffects.generatePolyLine(this.editing ? closedPoints : this.points);

        // Notify React/UI:
        this.emit('pointsChanged', this.getPolygonPositions());
    }

    /**
     * Rebuilds spheres to ensure proper indexing alignment.
     * Crucial after any point insertion or deletion operation
     */
    rebuildVisualElements() {
        this.visualEffects.removeSpheres();

        this.points.forEach((point) => {
            this.visualEffects.createSphere(point.clone());
        });

        this.visualEffects.removePolyLine();
    }

    closePolyline(targetPointIndex: number = 0) {
        if (this.points.length < this.minNumPoints) return false;
        if (this.onSelfIntersectionSave)
            if (polygonHasSelfIntersection(this.points, false)) {
                this.onSelfIntersectionSave?.();
                return false;
            }

        this.visualEffects.removePolyLine();

        // If the polygon is self-intersecting at this point put everything back to square one.

        const closedPoints = [...this.points, this.points[targetPointIndex]];
        this.visualEffects.generatePolyLine(closedPoints);
        this.visualEffects.highlightClosedState();

        this.pause();
        return true;
    }

    // =====================================
    // STATE CONTROL
    // =====================================

    start() {
        if (this.running) return;

        this.running = true;
        this.editing = this.points.length >= 3 ? true : false;
        this.visualEffects.setIsEditing(this.editing);

        this.eventHandler.deactivateTransformControls();

        this.visualEffects.hideCloseRadiusIndicator();

        // Attach listeners so user can interact again:
        this.eventHandler.attachEventListeners();

        // Notify React/UI:
        this.emit('started');
    }

    stop(forceClose?: boolean) {
        if (!this.running) return;

        const closed = this.closePolyline();
        if (closed) {
            this.onSaveCallback?.(this.points, false);
        } else {
            if (forceClose) {
                this.onSaveCallback?.(this.points, true);
                this.points = [];
            }

            this.emit('pointsChanged', this.getPolygonPositions());
            if (!forceClose) return;
        }

        // Visual cleanup:
        this.visualEffects.removeSpheres();
        this.visualEffects.hideCloseRadiusIndicator();

        // Disable interactions:
        this.eventHandler.deactivateTransformControls();
        this.eventHandler.detachEventListeners();

        this.running = false;
        this.editing = false;
        this.visualEffects.setIsEditing(this.editing);

        // Notify React/UI:
        this.emit('stopped');
    }

    // Cancel unfinished polygons and not create buildings.
    stopAndDiscard() {
        this.points = [];
        this.emit('pointsChanged', this.getPolygonPositions());

        this.visualEffects.removeSpheres();
        this.visualEffects.removePolyLine();
        this.visualEffects.hideCloseRadiusIndicator();

        this.eventHandler.deactivateTransformControls();
        this.eventHandler.detachEventListeners();

        this.running = false;
        this.editing = false;
        this.visualEffects.setIsEditing(this.editing);

        this.emit('stopped');
    }

    pause() {
        if (this.points.length === 0) return;

        const closedPoints = [...this.points, this.points[0]];
        this.visualEffects.removeSpheres();
        this.visualEffects.removePolyLine();
        this.visualEffects.generatePolyLine(closedPoints);
        this.visualEffects.hideCloseRadiusIndicator();

        this.running = false;
        this.editing = false;
        this.visualEffects.setIsEditing(this.editing);
    }

    // =====================================
    // GETTERS
    // =====================================

    getPolygonPositions() {
        return this.points.map((p) => p.clone());
    }

    isRunning() {
        return this.running;
    }
}
